import * as fabric from "fabric";
import { checkIfInsideClipRect } from "./Canvas/imagesUtils";


export const COMPLETE_SIZE_BASE = {
  h: { width: "1124", height: "800", aspectRatio: "1124/800" },
  v: { width: "800", height: "1124", aspectRatio: "800/1124" },
  s: { width: "800", height: "800", aspectRatio: "1/1" }
};

/**
 * Handles the resizing of the container element and updates the scale layout accordingly.
 *
 * This function is called when the window is resized. It retrieves the container element with the ID "capture",
 * calculates the new scale based on the container's width and height, and the base size for the current
 * orientation. It then dispatches an action to update the scale layout in the application state.
 */
export const handleResize = (dispatch, orientation) => {
  const container = document.getElementById("capture");

  if (container && orientation) {
    const newScale = calculateScale(
      COMPLETE_SIZE_BASE[orientation!].width,
      COMPLETE_SIZE_BASE[orientation!].height
    );
    dispatch({
      type: "editor/updateScaleLayout",
      payload: newScale
    });
  }
};

/**
 * Handles a mouse click event outside the canvas element and toolbar.
 *
 * This function determines if a click event occurred outside the specified canvas element
 * and toolbar. If so, it clears the active selection on the canvas instance and re-renders
 * the canvas to update its state.
 *
 * @param {MouseEvent} event - The mouse click event triggered by the user.
 * @param {Object} canvasInstance - The instance of the canvas, typically from a canvas library.
 * @param {HTMLElement} canvasElement - The DOM element associated with the canvas.
 * @param {HTMLElement} toolbar - The DOM element associated with the toolbar.
 */
export const handleClickOutsideCanvas = (event: MouseEvent, canvasInstance, canvasElement, toolbar) => {
  if (
    canvasInstance &&
    canvasElement &&
    toolbar &&
    !canvasElement.contains(event.target as Node) &&
    !toolbar.contains(event.target as Node)
  ) {
    // Désélectionner l'objet actif sur le canvas
    canvasInstance.discardActiveObject();
    canvasInstance.renderAll();
  }
};

/*
 * Calculates the appropriate scale factor to fit a layout within a given container, while respecting the aspect ratio of the layout and the available viewport size.

 * Parameters:
 * - `containerWidth`: The width of the container in which the layout will be displayed.
 * - `containerHeight`: The height of the container in which the layout will be displayed.
 * - `layoutWidth`: The width of the layout to be displayed.
 * - `layoutHeight`: The height of the layout to be displayed.
 *
 * Returns:
 * The scale factor to be applied to the layout to fit it within the container, while preserving the aspect ratio.
 */
export const calculateScale = (
  layoutWidth: number,
  layoutHeight: number
) => {
  const padding = 32;
  const globalContainer = document.getElementById("global-container");
  const referenceWidth = globalContainer ? globalContainer.clientWidth : window.innerWidth;
  const referenceHeight = globalContainer ? globalContainer.clientHeight : window.innerHeight;


  const widthRatio = (referenceWidth - padding) / layoutWidth;
  const heightRatio = (referenceHeight - padding) / layoutHeight;

  return Math.min(widthRatio, heightRatio);
};

/**
 * Ensures that the canvas viewport remains within the bounds of the visible canvas area
 * after applying zoom and panning transformations.
 *
 * @param {fabric.Canvas} canvas - The Fabric.js canvas instance to check and adjust the bounds for.
 *
 * The function calculates the zoomed dimensions of the canvas and uses the viewport
 * transformation matrix to determine its current position. If the viewport exceeds
 * the canvas boundaries (left, right, top, or bottom), it adjusts the transformation
 * matrix to ensure that the visible area remains within the allowed bounds.
 *
 * The viewport transformations are updated directly on the canvas instance.
 */
export const checkCanvasBounds = (canvas: fabric.Canvas) => {
  const viewPort = canvas.viewportTransform!;
  const zoom = canvas.getZoom();

  const canvasWidth = canvas.getWidth();
  const canvasHeight = canvas.getHeight();

  const widthZoomed = canvasWidth * zoom;
  const heightZoomed = canvasHeight * zoom;

  // X limits
  if (viewPort[4] > 0) viewPort[4] = 0;
  else if (viewPort[4] + widthZoomed < canvasWidth) viewPort[4] = canvasWidth - widthZoomed;

  // Y limits
  if (viewPort[5] > 0) viewPort[5] = 0; // Empêche de dépasser en haut
  else if (viewPort[5] + heightZoomed < canvasHeight) viewPort[5] = canvasHeight - heightZoomed; // Empêche de dépasser en bas

  canvas.setViewportTransform(viewPort);
};

/**
 * Resets the position of the fabric.js canvas to center its content within the available viewport,
 * accounting for the current zoom level.
 *
 * @param {fabric.Canvas} canvas - The fabric.js canvas instance to update.
 */
export const resetCanvasPosition = (canvas: fabric.Canvas) => {
  const canvasWidth = canvas.getWidth();
  const canvasHeight = canvas.getHeight();

  const zoom = canvas.getZoom();

  const totalWidth = canvasWidth * zoom;
  const totalHeight = canvasHeight * zoom;

  const viewPort = canvas.viewportTransform!;
  viewPort[4] = (canvasWidth - totalWidth) / 2;
  viewPort[5] = (canvasHeight - totalHeight) / 2;

  canvas.setViewportTransform(viewPort);
};


/**
 * Asynchronously initializes the canvas with the specified layout.
 *
 * This function locates the specified layout from a list of layouts and performs
 * necessary operations to set and initialize the canvas based on the selected layout.
 *
 * @param newLayout
 * @param {function} initializeCanvasChild - An asynchronous function to perform additional canvas initialization with
 *   the selected layout.
 * @returns {Promise<void>} A promise that resolves when the canvas initialization is complete.
 */
export const initializeCanvasWithLayout = async (
  newLayout,
  initializeCanvasChild: (layout) => Promise<void>
): Promise<void> => {
      await initializeCanvasChild(newLayout);
};

/**
 * Function to remove duplicate objects from the current Fabric.js canvas instance.
 *
 * This function ensures that objects with identical types and indices are removed
 * from the Fabric.js canvas, leaving only unique objects. It operates by constructing
 * a map of unique object identifiers, checking against this map, and removing duplicates
 * during iteration. After removing duplicates, the canvas is re-rendered to reflect the changes.
 *
 * Preconditions:
 * - The `fabricCanvasInstanceRef.current` must be defined and reference a valid Fabric.js canvas instance.
 *
 * Postconditions:
 * - Duplicate objects (based on their type and index) are removed from the canvas.
 * - The canvas is re-rendered to show the updated object state.
 */
export const removeDuplicateObjects = (canvas) => {
  if (!canvas) return;

  const objects = canvas.getObjects();

  const objectsByIdxAndType = new Map();

  objects.forEach((obj) => {
    if (obj.idx !== undefined) {
      const key = `${obj.idx}-${obj.type}`;
      if (!objectsByIdxAndType.has(key)) {
        objectsByIdxAndType.set(key, []);
      }
      objectsByIdxAndType.get(key).push(obj);
    }
  });

  objectsByIdxAndType.forEach((objArray, key) => {
    if (objArray.length > 1) {
      for (let i = 0; i < objArray.length - 1; i++) {
         canvas.remove(objArray[i]);
      }
    }
  });

  canvas.renderAll();
};



/*
 * This code block handles the initialization and management of the canvas element in the Template component. It includes the following functionality:
 *
 * - Initializes the canvas with the appropriate dimensions based on the selected layout and orientation.
 * - Adds event listeners to the canvas to handle user interactions, such as clearing the active object on click outside the canvas.
 * - Adjusts the position of objects on the canvas to accommodate the QR code, if enabled.
 * - Debounces the canvas resizing to improve performance.
 * - Provides methods to clear the canvas and initialize the canvas child elements (images and text boxes) based on the selected layout.
 */
export const clearCanvas = (canvas: fabric.Canvas) => {
  if (canvas) {
    canvas.getObjects().forEach((obj) => {
      canvas?.remove(obj);
    });
    canvas.requestRenderAll();
  }
};

/**
 * Handles the focus behavior for a Fabric.js textbox object on a canvas.
 * Switches focus to the specified textbox and enters editing mode if certain conditions are met.
 *
 * @param {fabric.Canvas} canvas - The Fabric.js canvas instance containing the objects.
 * @param {fabric.Textbox} textbox - The textbox object to focus and enter editing mode.
 * @return {void} Does not return any value.
 */
export function handleTextboxFocus(canvas: fabric.Canvas, textbox) {
  const activeObject = canvas.getActiveObject();

  if (activeObject && activeObject !== textbox && activeObject.type === 'textbox') {
    canvas.discardActiveObject();
    canvas.setActiveObject(textbox);

    setTimeout(() => textbox.enterEditing(), 0);
  } else if (!textbox.isEditing) {
    textbox.enterEditing();
  }
}

/**
 * Handles the mouse wheel event for zooming in and out on a Fabric.js canvas.
 *
 * @param {fabric.Canvas} canvas - The Fabric.js canvas instance to apply zooming functionality.
 * @returns {Function} - A function to handle the mouse wheel event, taking the event object as an argument.
 */
export const handleMouseWheel = (canvas: fabric.Canvas): Function => (opt: any) => {
  const delta = opt.e.deltaY;
  let zoom = canvas.getZoom();

  zoom = zoom - delta * 0.02;
  zoom = Math.max(1, Math.min(zoom, 2));

  // Fonction zoom. A corriger => Ne fonctionne pas sur les images zoomées, problèmes dans la conversion des
  const centerPoint = new fabric.Point(opt.e.offsetX, opt.e.offsetY);
  canvas.zoomToPoint(centerPoint, zoom);

  if (zoom === 1) {
    resetCanvasPosition(canvas);
  }

  opt.e.preventDefault();
  opt.e.stopPropagation();

  checkCanvasBounds(canvas);
};


/**
 * Handles the touch start event on a fabric.js canvas.
 *
 * This function is designed to handle single-touch drag and two-finger pinch gestures.
 * For single-touch events, it enables dragging if the canvas zoom level is greater than 1.
 * For two-finger touch events, it calculates and stores the initial distance between
 * the two touch points for pinch gesture detection.
 *
 * @param {fabric.Canvas} canvas - The fabric.js canvas instance on which the touch event occurs.
 * @param {Object} state - An object to maintain and update the state of interaction,
 * including dragging and pinching properties.
 * @return {Function} - A function that handles the `TouchEvent`, updating the interaction state
 * based on the type of gesture (single-touch or multi-touch).
 */
export const handleTouchStart =
  (canvas: fabric.Canvas, state: any): Function =>
    (e: TouchEvent) => {
      try {
        const touch = e.touches[ 0 ];
        const pointer = canvas.getPointer(touch);
        const target = canvas.getActiveObject();

        if (target) {
          state.isDraggingObject = true;

          if ( target && target.type === "textbox" ) {
            state.isDraggingObject = false;
            return;
          }
          if ( target && target.type === "image" ) {
            const isInsideClipRect = checkIfInsideClipRect(pointer, target.clipPath!);
            state.dragStartedInsideImage = isInsideClipRect;
            
            if ((target as any).placeholderImage) {
              e.preventDefault();
              state.isDraggingObject = false;
              state.isDragging = false;
              return;
            }
            
            if ((target as any)._originalElement?.currentSrc?.includes("ff783a41560d09c6477f0e293027ce29")) {
              e.preventDefault();
              state.initialLeft =  target.left;
              state.initialTop =  target.top;
              target.setCoords();
              canvas.renderAll();
            }
          }

          if (target && target.__corner) {
            state.isDraggingObject = false;
            state.isDragging = false;
            return;
          }
        }
        const zoom = canvas.getZoom();

        if ( zoom > 1 && e.touches.length === 1 ) {
          state.isDragging = true;
          state.lastPosition = { x: touch.clientX, y: touch.clientY };
          e.preventDefault();
        } else if ( e.touches.length === 2 ) {
          state.isPinching = true;
          const touch1 = e.touches[ 0 ];
          const touch2 = e.touches[ 1 ];
          state.startPinchDistance = Math.sqrt(
            Math.pow( touch2.clientX - touch1.clientX, 2 ) +
            Math.pow( touch2.clientY - touch1.clientY, 2 )
          );
          e.preventDefault();
        }
      } finally {
        canvas.requestRenderAll();
      }
    };

/**
 * Handles touch move events on a Fabric.js canvas, enabling pinch-to-zoom and dragging gestures.
 *
 * @param {fabric.Canvas} canvas - The Fabric.js canvas instance being interacted with.
 * @param {Object} state - The state object used to track gesture-related data.
 * @param {boolean} state.isPinching - Flag indicating if the user is performing a pinch gesture.
 * @param {boolean} state.isDragging - Flag indicating if the user is performing a drag gesture.
 * @param {number} state.startPinchDistance - The initial distance between two touch points during a pinch gesture.
 * @param {Object} state.lastPosition - The last recorded position of a single touch point during dragging.
 * @param {number} state.lastPosition.x - The x-coordinate of the last touch point.
 * @param {number} state.lastPosition.y - The y-coordinate of the last touch point.
 * @returns {Function} A function to be attached to the canvas touch move event, handling gesture interactions.
 */
export const handleTouchMove =
  (canvas: fabric.Canvas, state: any): Function =>
    (e: TouchEvent) => {
      try {
        const zoom = canvas.getZoom();
        const touch = e.touches[ 0 ];
        const target = canvas.getActiveObject();

        if ( state.isPinching && e.touches.length === 2 ) {
          // Pinch-to-zoom
          const touch1 = e.touches[ 0 ];
          const touch2 = e.touches[ 1 ];
          const currentPinchDistance = Math.sqrt(
            Math.pow( touch2.clientX - touch1.clientX, 2 ) +
            Math.pow( touch2.clientY - touch1.clientY, 2 )
          );

          const scaleChange = currentPinchDistance / state.startPinchDistance;
          let newZoom = canvas.getZoom() * scaleChange;
          newZoom = Math.max( 1, Math.min( newZoom, 2 ) );

          // Fonction zoom. A corriger => Ne fonctionne pas sur les images zoomées, problèmes dans la conversion des
          // coordonnés const midPoint = new fabric.Point( (touch1.clientX + touch2.clientX) / 2, (touch1.clientY +
          // touch2.clientY) / 2 );

          // canvas.zoomToPoint( midPoint, newZoom );

          if ( newZoom === 1 ) {
            resetCanvasPosition( canvas );
          }

          state.startPinchDistance = currentPinchDistance;
          e.preventDefault();
        } else if ( state.isDraggingObject && target ) {

          if ( !target._originalElement.currentSrc.includes( "83a41560d09c6477f0e293027ce" ) ) {
            if ( !state.dragStartedInsideImage ) {
              canvas.discardActiveObject();
              state.isDraggingObject = false;
              e.preventDefault();
              return;
            }
          } else {
            target.top = state.initialTop;
            target.left = state.initialLeft;
            target.setCoords();
            canvas.renderAll();
          }

          // Déplace l'objet actif
          const deltaX = (touch.clientX - state.lastPosition.x) / zoom;
          const deltaY = (touch.clientY - state.lastPosition.y) / zoom;

          target.left = (target.left || 0) + deltaX;
          target.top = (target.top || 0) + deltaY;
          target.setCoords();

          canvas.renderAll(); // Mise à jour visuelle du canvas

          state.lastPosition = { x: touch.clientX, y: touch.clientY };
          // e.preventDefault();

        } else if ( state.isDragging && zoom > 1 ) {
          // Pan global
          const touch = e.touches[ 0 ];
          const deltaX = touch.clientX - state.lastPosition.x;
          const deltaY = touch.clientY - state.lastPosition.y;

          state.lastPosition = { x: touch.clientX, y: touch.clientY };

          const vpt = canvas.viewportTransform!;
          vpt[ 4 ] += deltaX;
          vpt[ 5 ] += deltaY;
          canvas.setViewportTransform( vpt );

          checkCanvasBounds( canvas );
        }
      } finally {
        canvas.requestRenderAll();
      }

    }

/**
 * An event handler function for the end of a touch interaction.
 *
 * This function resets the `isPinching` and `isDragging` properties in the
 * provided state object to `false`, effectively stopping any ongoing pinch
 * or drag interactions.
 *
 * @param canvas
 * @param {any} state - The state object used to track interaction flags such as
 *                      `isPinching` and `isDragging`.
 * @returns {Function} A function that, when executed, updates the state object.
 */
export const handleTouchEnd = ( canvas: fabric.Canvas, state: any ): Function => () => {
  try {
    state.isDragging = false;
    state.isDraggingObject = false;
    state.isPinching = false;
  } finally {
    canvas.requestRenderAll();
  }
};

/**
 * Event handler for the mouseover event on a Fabric.js canvas.
 *
 * This function is a higher-order function that returns another function to handle the mouseover event.
 * When a mouseover event occurs, it checks whether the target of the event is a textbox object.
 * If so, it dynamically creates a rectangular hover outline around the textbox object to
 * visually indicate that the mouse is over it.
 *
 * @param {fabric.Canvas} canvas - The Fabric.js canvas instance on which the mouseover event is being handled.
 * @returns {(event: any) => void} - A function that handles the mouseover event when invoked.
 */
export const handleMouseOver = (canvas: fabric.Canvas): (event: any) => void => (event: any) => {
  const target = event.target;

  if (target && target.type === "textbox") {
    target.hoverRect = new fabric.Rect({
      left: target.left,
      top: target.top,
      width: target.width,
      height: target.maxHeight,
      originX: target.originX,
      originY: target.originY,
      absolutePositioned: true,
      stroke: "blue",
      strokeWidth: 2,
      fill: "transparent",
      selectable: false,
      evented: false,
    });

    canvas.add(target.hoverRect);
    canvas.renderAll();
  }
};

/**
 * Handles the mouse out event for a Fabric.js canvas.
 *
 * This function is triggered when the mouse pointer leaves an interactive object on the canvas.
 * It specifically removes hover-based visual overlays (such as rectangle highlights) from
 * objects of type "textbox" and ensures the canvas is re-rendered to reflect the changes.
 *
 * @param {fabric.Canvas} canvas - An instance of a Fabric.js canvas on which the event is being handled.
 * @returns {Function} A function that processes the event when the mouse pointer leaves an object.
 */
export const handleMouseOut = (canvas: fabric.Canvas): Function => (event: any) => {
  const target = event.target;

  if (target && target.type === "textbox") {
    if (target.hoverRect) {
      canvas.remove(target.hoverRect);
      target.hoverRect = null;
    }
    canvas.renderAll();
  }
};